import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Colors } from 'modules/theme';
import Loader from 'components/Loader';
import H5 from 'components/H5';
import { getXAxisInterval } from 'utils/flowVolumeChart';
import {
  CHART_TOOLTIP_CURSOR_LINE,
  CHART_TOOLTIP_WRAPPER_STYLE,
  MetricLineTooltip,
} from './chartTooltip';
import { ChartPanel } from './flowVolume.styles';

const CHART_AXIS = '#94a3b8';
const CHART_GRID = '#e3edf5';

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(248, 251, 255, 0.82);
  z-index: 10;
  border-radius: 12px;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #e8f1f8;
  box-shadow: 0 4px 16px rgba(20, 152, 203, 0.1);
`;

const RoMetricLineChart = ({ id, data, loading, valueLabel, referenceAreas }) => {
  const chartData = loading ? [] : data;
  const xAxisInterval = getXAxisInterval(chartData.length);

  return (
    <ChartPanel id={id}>
      {loading && (
        <LoadingOverlay>
          <LoadingCard>
            <Loader size={30} />
            <H5 color={Colors.gray8} style={{ marginTop: 10, margin: 0 }}>
              Loading chart data...
            </H5>
          </LoadingCard>
        </LoadingOverlay>
      )}
      <ResponsiveContainer width="100%" height="100%" debounce={50}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 4 }}>
          <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 6" vertical={false} />
          <XAxis
            dataKey="name"
            scale="point"
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            tick={{ fontSize: 11, fill: CHART_AXIS }}
            interval={xAxisInterval}
            tickMargin={10}
            padding={{ left: 16, right: 16 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            tick={{ fontSize: 11, fill: CHART_AXIS }}
            width={48}
          />
          {referenceAreas.map((area, index) => (
            <ReferenceArea
              key={`ref-${index}`}
              y1={area.y1}
              y2={area.y2}
              ifOverflow="extendDomain"
              fill={area.fill}
            />
          ))}
          <Tooltip
            content={<MetricLineTooltip valueLabel={valueLabel} />}
            cursor={CHART_TOOLTIP_CURSOR_LINE}
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
          />
          <Line
            type="monotone"
            dataKey="gal"
            stroke={Colors.lightBlue}
            strokeWidth={2}
            dot={false}
            isAnimationActive={!loading}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
};

RoMetricLineChart.propTypes = {
  data: PropTypes.array,
  id: PropTypes.string,
  loading: PropTypes.bool,
  referenceAreas: PropTypes.array,
  valueLabel: PropTypes.string,
};

RoMetricLineChart.defaultProps = {
  data: [],
  id: 'roMetricChart',
  loading: false,
  referenceAreas: [],
  valueLabel: 'PPM',
};

export default RoMetricLineChart;
